<template>
  <div class="details-table">
    <div class="headers">
      <div></div>
      <div v-for="(header, index) in headers" :key="index" class="grid-item headers-items">
        <div class="header-group" :class="index === 4 && 'teste'">
          <span class="header-text">{{ header.text }}</span>
          <span v-if="header.description.length > 0" class="header-description">{{ header.description }}</span>
        </div>
      </div>
    </div>
    <div class="clients-data">
      <div class="header-text">Núm. de clientes</div>
      <div v-for="(clientMetric, index) in clients_values" :key="`${clientMetric}-${index}-client`" class="grid-item">
        {{ localeString(clientMetric) }}
      </div>
    </div>
    <div class="revenue-data">
      <div class="header-text">Fat. Produtos</div>
      <div v-for="(revenueMetric, index) in products_revenues_values" :key="`${revenueMetric}-${index}-product-revenue`" class="grid-item">
        {{ revenueMetric | localeString(0) | currency }}
      </div>
    </div>
    <div class="increment-data">
      <div class="header-text">Incremento</div>
      <div v-for="(incrementMetric, index) in increment_values" :key="`${incrementMetric}-${index}-increment`" class="grid-item increment">
        + {{ incrementMetric | localeString(0) | currency }}
      </div>
    </div>
    <div class="discount-data">
      <div class="header-text">Descontos</div>
      <div v-for="(discountMetric, index) in discount_values" :key="`${discountMetric}-${index}-discount`" class="grid-item discount">
        - {{ discountMetric | localeString(0) | currency }}
      </div>
    </div>
  </div>
</template>

<script>
import { abbreviation, localeString } from '@/config/helpers.js'
import { mapGetters } from 'vuex'
export default {
  name: 'DreIncrementalRevenueTable',
  props: {
    variant: {
      type: String,
      default: 'offer-products',
      validator: (value) => ['offer-products', 'out-club-products'],
    },
    products: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters(['dreRevenueIncrementMetrics', 'analyticsConfiguration']),
    metrics() {
      return this.dreRevenueIncrementMetrics?.detailsMetrics?.clusterMetrics
    },
    totalMetrics() {
      return this.dreRevenueIncrementMetrics?.detailsMetrics?.total
    },
    actives() {
      return this.metrics?.actives
    },
    firstBuy() {
      return this.metrics?.firstBuy
    },
    inactives() {
      return this.metrics?.inactives
    },
    losses() {
      return this.metrics?.losses
    },
    clients_values() {
      if (this.totalMetrics) {
        const { totalClients } = this.totalMetrics

        return [this.actives?.totalClients, this.inactives?.totalClients, this.losses?.totalClients, this.firstBuy?.totalClients, totalClients]
      } else return []
    },
    products_revenues_values() {
      if (this.totalMetrics) {
        const { productsRevenue } = this.totalMetrics

        return [this.actives?.productsRevenue, this.inactives?.productsRevenue, this.losses?.productsRevenue, this.firstBuy?.productsRevenue, productsRevenue]
      } else return []
    },
    increment_values() {
      if (this.totalMetrics) {
        const { increment } = this.totalMetrics

        return [this.actives?.increment, this.inactives?.increment, this.losses?.increment, this.firstBuy?.increment, increment]
      } else return []
    },
    discount_values() {
      if (this.totalMetrics) {
        const { discount } = this.totalMetrics

        return [this.actives?.discount, this.inactives?.discount, this.losses?.discount, this.firstBuy?.discount, discount]
      } else return []
    },
    headers() {
      return [
        {
          text: 'Clientes recorrentes',
          description: 'Clientes do clube que compraram algum dos produtos ofertados nos 90 dias anteriores a análise.',
        },
        {
          text: 'Upsell',
          description: 'Clientes do clube que não compraram os produtos ofertados nos 90 dias anteriores a análise, mas compraram outros produtos.',
        },
        {
          text: 'Clientes recuperados',
          description: 'Clientes do clube perdidos que não compraram nos 90 dias anteriores a análise.',
        },
        {
          text: 'Novos clientes',
          description: 'Clientes do clube que realizaram a primeira compra no período da análise.',
        },
        {
          text: 'Total',
          description: '',
        },
      ]
    },
    headerTooltips() {
      return [
        {
          title: 'Clientes recorrentes',
          description: 'Clientes que compraram algum dos produtos ofertados nos 90 dias anteriores a análise.',
        },
        {
          title: 'Upsell',
          description: 'Clientes que não compraram os produtos ofertados nos 90 dias anteriores a análise, mas compraram outros produtos.',
        },
        {
          title: 'Clientes recuperados',
          description: 'Clientes perdidos que não compraram nos 90 dias anteriores a análise.',
        },
        {
          title: 'Novos clientes',
          description: 'Clientes que realizaram a primeira compra no período da análise.',
        },
        {
          title: '',
          description: '',
        },
      ]
    },
    incrementTooltips() {
      return [
        {
          title: 'Clientes recorrentes',
          description:
            'É calculado com base em um aumento no consumo habitual desses clientes. Este cálculo é feito através da média diária de compras do produto específico ofertado por cada cliente nos últimos 90 dias no clube. Se um cliente excede essa média durante o período de uma oferta, interpretamos esse aumento como um incremento. Este incremento pode ser o resultado de uma variedade de fatores, incluindo a atratividade da oferta, as comunicações feitas ao cliente, entre outros impulsionadores de consumo.',
        },
        {
          title: 'Upsell',
          description:
            'São clientes que experimentaram produtos em oferta que não constavam em seu histórico de compras dos últimos 90 dias no clube. Essa mudança no comportamento de compra pode ser atribuída à atratividade da oferta, ao impacto das comunicações direcionadas ou a outros fatores que despertaram o interesse do cliente.',
        },
        {
          title: 'Clientes recuperados',
          description:
            'É calculado com base na recuperação de clientes que estavam perdidos, ou seja, que não fizeram compras nos últimos 90 dias no clube. Lembrando que estamos considerando todo o incremento da venda, pois esse cliente estava perdido, ou seja, poderia não realizar nenhuma compra se não fosse recuperado pela oferta. A recuperação desses clientes pode ser atribuída a vários fatores, como a atratividade da oferta e o impacto das comunicações realizadas',
        },
        {
          title: 'Novos clientes',
          description:
            'É calculado com base na conquista de novos clientes, ou seja, que não haviam realizado compras até então no clube. Lembrando que estamos considerando todo o incremento da venda, pois esse cliente foi convertido pela oferta. A conquista desses clientes pode ser atribuída a diversos fatores, como a atratividade da oferta, o impacto das comunicações realizadas.',
        },
        {
          title: '',
          description: '',
        },
      ]
    },
    isEmpty() {
      return this.metrics === undefined
    },
  },
  methods: {
    abbreviation,
    localeString,
  },
}
</script>

<style lang="scss" scoped>
.details-table {
  display: grid;
  .headers,
  .clients-data,
  .revenue-data,
  .increment-data,
  .discount-data {
    height: 52px;
    display: grid;
    grid-template-columns: 130px 158px 168px 146px 154px 150px;
    gap: 20px;
    align-items: center;
  }
  .headers {
    align-items: flex-end;
    height: auto !important;
    .header-group {
      display: flex;
      flex-direction: column;
      height: 118px;
      padding: 0 12px;
      border-left: solid 1px $cinza-l80;
      gap: 8px;
      justify-content: flex-end;
      padding-bottom: 10px;
      .header-description {
        color: $cinza-0;
        font-size: 10px;
        font-weight: 400;
        letter-spacing: 0.167px;
      }
    }
  }
  .clients-data,
  .revenue-data,
  .increment-data,
  .discount-data {
    border-top: 1px solid #d2d6d9;
  }
}
.header-text {
  color: $cinza-d20;
  font-size: 12px;
  font-weight: 700;
}

::v-deep .v-data-table th {
  padding: 15px 8px;
  span {
    color: $cinza-d20;
    font-size: 12px;
    font-weight: 700;
  }
}

.grid-item:not(.headers-items) {
  border-left: solid 1px $cinza-l80;
  padding: 0 12px;
  height: 100%;
  display: flex;
  align-items: center;
  color: $cinza-0;
}

::v-deep .increment {
  color: $verde-0 !important;
}

::v-deep .discount {
  color: $vermelho-0 !important;
}
</style>
